import { styled } from '@uhg-abyss/web/tools/styled';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Popover } from '@uhg-abyss/web/ui/Popover';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { FilterCollectionModel, FilterOptionsType } from '../Constants';

const Container = styled('div', {
  background: '$core.color.neutral.0',
  marginTop: '16px',
  paddingLeft:
    'var(--abyss-space-modalDialog-spacing-padding-horizontal-header)',
  paddingRight:
    'var(--abyss-space-modalDialog-spacing-padding-horizontal-header)',
  paddingBottom: '16px',
});

type Options = {
  label: String | ReactNode;
  value: string | number | boolean;
  dnt_label: string;
  c_value?: number;
};

interface Props {
  defaultValue?: string | number | boolean;
  disableSubmitBtn?: boolean;
  isDisabled?: boolean;
  locationForAnalytics?: string;
  model: string;
  onApply?: (value: string | number | boolean) => void;
  onOptionChange?: (any) => void;
  options: Array<Options>;
  selectInputLabel?: string | undefined;
}

export const FilterDropDown = ({
  defaultValue,
  isDisabled,
  locationForAnalytics,
  model,
  onOptionChange = () => {},
  options,
  selectInputLabel,
}: Props) => {
  const { t } = useTranslation();

  function onChangeDropdownValue(val) {
    if (val) {
      adobeLinkTrackEvent({
        name: options.filter((option) => val === option.value.toString())?.[0]
          ?.dnt_label,
        location: locationForAnalytics,
        type: FilterOptionsType.DROPDOWN,
      });
    }
    onOptionChange({
      [model]: options.filter((option) => val === option.value),
    });
  }

  const sortedOptions = options.sort(
    (a, b) => (a?.c_value ?? 0) - (b?.c_value ?? 0)
  );
  const selectedOption = sortedOptions.find(
    (option) => option.value === defaultValue
  );
  const isDistance = model === FilterCollectionModel.DISTANCE;

  const inputValue = isDisabled
    ? t('Not applicable')
    : selectedOption
    ? selectedOption?.value
    : '';

  const inputOptions = isDisabled
    ? [
        {
          label: t('Not applicable'),
          value: t('Not applicable'),
        },
      ]
    : sortedOptions.map((option) => ({
        label: option.label?.toString(),
        value: option.value,
      }));

  const tooltipContent =
    isDistance && isDisabled
      ? t('CONTENT_FILTERS.DISTANCE_DISABLED_TOOLTIP')
      : isDisabled
      ? t('CONTENT_FILTERS.GENERIC_FILTER_DISABLED_TOOLTIP')
      : t('DISTANCE_POPOVER_CONTENT');

  const selectInput = (
    <SelectInput
      css={{
        'abyss-select-input-menu-expand-icon': {
          overflow: 'hidden',
          width: 0,
          height: 0,
        },
      }}
      data-auto-testid="select-input-menu"
      data-testid="select-input-section"
      enableOutsideScroll
      helper={
        isDistance ? (
          <Popover
            content={tooltipContent}
            title={t('CONTENT_FILTERS.DISTANCE')}
          >
            <IconSymbol
              color="$web.semantic.color.icon.status.info"
              icon="help"
              size="$input-header.sizing.all.icon"
              variant="outlined"
            />
          </Popover>
        ) : null
      }
      isDisabled={isDisabled}
      label={t(selectInputLabel ?? '') ?? ''}
      onChange={(newValue) => {
        onChangeDropdownValue(newValue);
      }}
      options={inputOptions}
      value={inputValue}
    />
  );

  return (
    <Container
      css={{
        // paddingBottom: '6px', // to allow space for bottom border when SelectInput is highlighted
        borderBottom: '1px solid $core.color.neutral.30',
      }}
      data-auto-testid="filter-dropdown-section"
      data-testid="filter-dropdown-section"
    >
      {isDisabled ? (
        <Tooltip content={tooltipContent}>
          <span style={{ display: 'inline-flex', width: '100%' }}>
            {selectInput}
          </span>
        </Tooltip>
      ) : (
        selectInput
      )}
    </Container>
  );
};
